import { authExpireWindow } from '~/config/settings/worktango.settings';

const atob = window.atob;

/**
 * Sanitize a token string so only the actual cryptographic token remains.
 *
 * Take a token (that is potentially formated in a way that it is a string with
 * more than just the token) and return *only* the token part of it, effectively
 * "sanitizing" it down to just the token part of the string.
 *
 * Some code appends "JWT" to the beginning of the token so that it looks like
 * `JWT aaaa.bbbb.cccc`. This simply ensures that only the "token" part is
 * returned so you a guaranteed to get just `aaaa.bbbb.cccc`.
 */
const sanitize = (token) => {
  const splitToken = token.split(' ');

  if (splitToken.length === 1) return token;

  return splitToken[1];
};

/**
 * Decode base64 content from JWT and return transformed values.
 *
 * NOTE: signature is not base64 (it is a cryptographic string) and as such does
 * not require decoding because there is nothing to decode.
 */
const parse = (token) => {
  const sanitizedToken = sanitize(token);
  const [headerBase64, payloadBase64, signature] = sanitizedToken.split('.');

  // Return an empty object if token could not be split as expected.
  if (!headerBase64 || !payloadBase64 || !signature) return {};

  const header = JSON.parse(atob(headerBase64));
  const payload = JSON.parse(atob(payloadBase64));

  return {
    header,
    payload,
    signature,
  };
};

/**
 * Parse individual parts of JWT.
 */
const parseHeader = (token) => parse(token).header;
const parsePayload = (token) => parse(token).payload;
const parseSignature = (token) => parse(token).signature;

/**
 * Consider the token expired if within (or out of) expiration window to buffer
 * enough time for the token to be used successfully (e.g., a user's session).
 *
 * NOTE: Using config module here, but only to set the default value. The rest
 * of the logic is standalone and can be overriden by providing a param value).
 */
const isExpired = (token = '', expireWindow = authExpireWindow) => {
  if (!token) return false;

  const nowInSeconds = Date.now() / 1000;
  const payload = parsePayload(token);

  return payload.exp <= nowInSeconds + expireWindow;
};

export const jwt = {
  parse,
  parseHeader,
  parsePayload,
  parseSignature,
  isExpired,
  sanitize,
};
